import { Auction, PotentialReceiversTablePayload } from "core/types";
import React, { useEffect, useReducer } from "react";
import { getCheckboxSettings } from "../shared";

export const ACTION = {
  DESELECT_ALL_REQUESTS: "DESELECT_ALL_REQUESTS",
  SET_AUTOMATIC_SEARCH: "SET_AUTOMATIC_SEARCH",
  SET_LIST: "SET_LIST",
  SET_PATIENT_PREFERRED: "SET_PATIENT_PREFERRED",
  SET_REQUEST: "SET_REQUEST",
} as const;

export type RequestListType = {
  careprovider_id: number;
  id: number;
  manually_selected: boolean;
  patient_preferred: boolean;
  patient_preferred_reason: string | undefined;
};

export type PatientPreferredInput = Pick<
  RequestListType,
  "id" | "patient_preferred_reason" | "patient_preferred"
>;

type RequestFormState = Record<number, RequestListType>;

export type RequestListReducerActionTypes =
  | {
      payload: { id: number };
      type: typeof ACTION.SET_REQUEST;
    }
  | {
      payload: PatientPreferredInput;
      type: typeof ACTION.SET_PATIENT_PREFERRED;
    }
  | {
      payload: RequestFormState;
      type: typeof ACTION.SET_LIST;
    }
  | {
      type: typeof ACTION.SET_AUTOMATIC_SEARCH;
    }
  | { type: typeof ACTION.DESELECT_ALL_REQUESTS };

export type RequestListState = {
  automaticSearch: boolean;
  requestFormState: RequestFormState;
};

export function requestListReducer(
  requestListState: RequestListState,
  action: RequestListReducerActionTypes,
): RequestListState {
  const newState = { ...requestListState };

  switch (action.type) {
    case ACTION.SET_REQUEST: {
      if (newState.automaticSearch) return newState;

      const currentState = newState.requestFormState?.[action.payload.id];
      if (currentState) {
        newState.requestFormState[action.payload.id] = {
          ...currentState,
          manually_selected: !currentState.manually_selected,
        };
      }

      return newState;
    }
    case ACTION.SET_PATIENT_PREFERRED: {
      const currentState = newState.requestFormState?.[action.payload.id];
      if (currentState) {
        newState.requestFormState[action.payload.id] = {
          ...currentState,
          patient_preferred: action.payload.patient_preferred,
          patient_preferred_reason: action.payload.patient_preferred_reason,
        };
      }
      return newState;
    }
    case ACTION.DESELECT_ALL_REQUESTS:
      Object.keys(newState.requestFormState).forEach((requestId) => {
        if (newState.requestFormState[Number(requestId)].manually_selected) {
          newState.requestFormState[Number(requestId)].manually_selected =
            false;
          newState.requestFormState[Number(requestId)].patient_preferred =
            false;
          newState.requestFormState[
            Number(requestId)
          ].patient_preferred_reason = undefined;
        }
      });

      return newState;
    case ACTION.SET_LIST:
      newState.requestFormState = action.payload;
      return newState;
    case ACTION.SET_AUTOMATIC_SEARCH:
      newState.automaticSearch = !newState.automaticSearch;
      return newState;
    default:
      return newState;
  }
}

export const useRequestListReducer = (
  auction: Auction,
  tableData: PotentialReceiversTablePayload | undefined,
): [RequestListState, React.Dispatch<RequestListReducerActionTypes>] => {
  const [requestListState, dispatch] = useReducer(requestListReducer, {
    automaticSearch: getCheckboxSettings({ auction }).autoSelectHeader,
    requestFormState: {},
  });

  useEffect(() => {
    if (!tableData?.requests?.length) return;

    const providerList = {
      ...requestListState.requestFormState,
    };
    tableData.requests.forEach((request) => {
      providerList[request.id] = {
        manually_selected: false,
        patient_preferred: false,
        patient_preferred_reason: undefined,
        // if the table was accessed before
        //  we could have some existing state
        ...(providerList?.[request.id] ? providerList[request.id] : {}),
        careprovider_id: request.careprovider.id,
        // these values are fixed and should always be filled, this ensures they are
        id: request.id,
      };
    });

    dispatch({
      payload: providerList,
      type: ACTION.SET_LIST,
    });
  }, [tableData?.requests]);

  return [requestListState, dispatch];
};
